import { useCallback, useEffect, useRef } from "react";

type BackButtonDetectorOptions = {
  onBackButtonClick: () => void;
};

const GUARD_STATE = { guard: true, reason: "back-button-detection" };

export function useUserBackButtonClickDetector({ onBackButtonClick }: BackButtonDetectorOptions) {
  const isAlreadyDetectedRef = useRef(false);
  const isDetectionStartedRef = useRef(false);
  const onBackButtonClickRef = useRef(onBackButtonClick);
  const handlePopStateRef = useRef<(() => void) | null>(null);

  onBackButtonClickRef.current = onBackButtonClick;

  const pushGuardState = useCallback(() => {
    try {
      if (window.history.length > 1) {
        window.history.pushState(GUARD_STATE, "", window.location.href);
      }
    } catch (err) {
      // Silently fail if pushState is not available
    }
  }, []);

  const handlePopState = useCallback(() => {
    if (!isAlreadyDetectedRef.current) {
      onBackButtonClickRef.current?.();
      isAlreadyDetectedRef.current = true;
    }
  }, []);

  handlePopStateRef.current = handlePopState;

  const startDetection = useCallback(() => {
    if (isDetectionStartedRef.current) return;

    isDetectionStartedRef.current = true;
    pushGuardState();
    window.addEventListener("popstate", handlePopState);
  }, [pushGuardState, handlePopState]);

  const triggerUserBackButtonClickMonitoring = useCallback(() => {
    if (!isDetectionStartedRef.current) {
      startDetection();
    }
  }, [startDetection]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      isDetectionStartedRef.current = false;
      if (handlePopStateRef.current) {
        window.removeEventListener("popstate", handlePopStateRef.current);
      }
    },
    []
  );

  return {
    triggerUserBackButtonClickMonitoring
  };
}
